import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { leadsApi, BulkUploadJobDto, PaginatedBulkUploadJobLogs } from '../api/leadsApi'
import { queryClient } from '../queryClient'
import Papa from 'papaparse'

export type CsvRow = { [col: string]: string }

export interface ValidationIssue {
  row: number
  field: string
  message: string
  severity: 'error' | 'warning'
}

export class BulkUploadStore {
  // UI states
  open = false
  step = 0 // 0: Upload, 1: Preview, 2: Processing, 3: Done
  
  // File states
  selectedFile: File | null = null
  fileError: string | null = null
  isDragging = false
  previewRows: CsvRow[] = []
  allRows: CsvRow[] = []
  rowCount = 0
  validationIssues: ValidationIssue[] = []

  // SuperAdmin / OrgAdmin tenant selection
  selOrgId = ''
  selLobId = ''

  // Upload state
  uploadPct = 0
  isUploading = false
  uploadError: { title: string; detail?: string } | null = null
  private uploadController: AbortController | null = null

  // Job states
  jobId: string | null = null
  jobStatus: BulkUploadJobDto | null = null
  elapsedSeconds = 0
  logsPage = 1
  logsResult: PaginatedBulkUploadJobLogs | null = null

  // Modals & Guidelines
  confirmOpen = false
  showGuidelines = false

  // Timers and intervals for polling
  private pollIntervalId: any = null
  private timerId: any = null

  constructor() {
    makeAutoObservable(this)
  }

  // Computed properties
  get isImporting() {
    return this.step === 2 && this.jobId !== null && 
      (!this.jobStatus || this.jobStatus.status === 'Queued' || this.jobStatus.status === 'Processing' || this.jobStatus.status === 'CancelRequested')
  }

  get isActive() {
    return this.jobId !== null && (this.step === 2 || this.step === 3)
  }

  get hasBlockingErrors() {
    return this.validationIssues.some(i => i.severity === 'error')
  }

  get validRowCount() {
    return this.rowCount - this.validationIssues.filter(i => i.severity === 'error').length
  }

  // Actions
  setOpen(val: boolean, quiet = false) {
    if (val && this.isImporting && !quiet) {
      toast.warn("Import is in process, try after it is done.", { toastId: 'import-in-progress' })
    }
    this.open = val
  }

  setStep(val: number) {
    this.step = val
  }

  setSelectedFile(file: File | null) {
    this.selectedFile = file
  }

  setFileError(err: string | null) {
    this.fileError = err
  }

  setIsDragging(val: boolean) {
    this.isDragging = val
  }

  setPreviewRows(rows: CsvRow[]) {
    this.previewRows = rows
  }

  setAllRows(rows: CsvRow[]) {
    this.allRows = rows
  }

  setRowCount(val: number) {
    this.rowCount = val
  }

  setValidationIssues(issues: ValidationIssue[]) {
    this.validationIssues = issues
  }

  setSelOrgId(val: string) {
    this.selOrgId = val
    this.selLobId = ''
  }

  setSelLobId(val: string) {
    this.selLobId = val
  }

  setConfirmOpen(val: boolean) {
    this.confirmOpen = val
  }

  setShowGuidelines(val: boolean) {
    this.showGuidelines = val
  }

  setLogsPage(val: number) {
    this.logsPage = val
  }

  setUploadError(val: { title: string; detail?: string } | null) {
    this.uploadError = val
  }

  reset() {
    this.step = 0
    this.selectedFile = null
    this.fileError = null
    this.isDragging = false
    this.previewRows = []
    this.allRows = []
    this.rowCount = 0
    this.validationIssues = []
    this.selOrgId = ''
    this.selLobId = ''
    this.uploadPct = 0
    this.isUploading = false
    this.uploadError = null
    this.uploadController = null
    this.jobId = null
    this.jobStatus = null
    this.elapsedSeconds = 0
    this.logsPage = 1
    this.logsResult = null
    this.confirmOpen = false
    this.showGuidelines = false
    this.stopPolling()
    this.stopTimer()
  }

  // API Actions
  async handleFileInput(file: File) {
    this.setFileError(null)
    this.setValidationIssues([])
    if (!file.name.endsWith('.csv')) {
      this.setFileError('Only .csv files are accepted. Please download the template and use it.')
      return
    }
    const MAX_FILE_BYTES = 100 * 1024 * 1024 // 100 MB
    if (file.size > MAX_FILE_BYTES) {
      this.setFileError(`File is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 100 MB.`)
      return
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.replace(/\s*\*\s*$/, '').trim(),
      complete: (res) => {
        const headers: string[] = res.meta.fields || []
        const missing = ['Name', 'Phone'].filter(h => !headers.includes(h))
        if (missing.length) {
          runInAction(() => {
            this.fileError = `Missing required columns: ${missing.join(', ')}. Please use our CSV template.`
          })
          return
        }

        const rows = res.data as CsvRow[]
        if (rows.length === 0) {
          runInAction(() => {
            this.fileError = 'The file has no data rows. Please add at least one lead and re-upload.'
          })
          return
        }

        const PHONE_REGEX = /^[6-9][0-9]{9}$/
        const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
        const issues: ValidationIssue[] = []
        const seenPhones = new Set<string>()
        const seenEmails = new Set<string>()

        rows.forEach((row, idx) => {
          const rowNum = idx + 2
          const name = (row['Name'] || '').trim()
          const phoneRaw = (row['Phone'] || '').trim()
          const email = (row['Email'] || '').trim()
          const gst = (row['Gst'] || '').trim()

          if (!name) {
            issues.push({ row: rowNum, field: 'Name', message: 'Name is required.', severity: 'error' })
          }

          if (!phoneRaw) {
            issues.push({ row: rowNum, field: 'Phone', message: 'Phone number is required.', severity: 'error' })
          } else {
            const phone = phoneRaw.replace(/\D/g, '')
            if (!PHONE_REGEX.test(phone)) {
              issues.push({
                row: rowNum, field: 'Phone',
                message: `'${phoneRaw}' is not a valid Indian mobile number (10 digits, starting with 6–9).`,
                severity: 'error',
              })
            } else {
            // Phone number validated (duplicate phone check removed)
            seenPhones.add(phone)
            }
          }

          if (email && !email.includes('@')) {
            issues.push({
              row: rowNum, field: 'Email',
              message: `'${email}' is not a valid email address.`,
              severity: 'error',
            })
          }

          if (gst) {
            const gstNorm = gst.toUpperCase()
            if (!GST_REGEX.test(gstNorm)) {
              issues.push({
                row: rowNum, field: 'GST',
                message: `'${gst}' is not a valid GSTIN. Format: 27AADCB2230M1Z2 (15 characters).`,
                severity: 'error',
              })
            }
          }
        })

        runInAction(() => {
          this.selectedFile = file
          this.previewRows = rows.slice(0, 5)
          this.allRows = rows
          this.rowCount = rows.length
          this.validationIssues = issues
        })
      },
      error: (err) => {
        runInAction(() => {
          this.fileError = `Failed to read file: ${err.message}`
        })
      }
    })
  }

  async startImport(fixedMappings: Record<string, string>, resolvedOrgId?: string, resolvedLobId?: string) {
    if (!this.selectedFile) return
    
    runInAction(() => {
      this.isUploading = true
      this.uploadPct = 0
      this.uploadError = null
      this.uploadController = new AbortController()
    })

    try {
      const csv = Papa.unparse(this.allRows)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const fileToUpload = new File([blob], this.selectedFile.name, { type: 'text/csv' })

      const result = await leadsApi.bulkUpload(fileToUpload, fixedMappings, {
        orgId: resolvedOrgId || undefined,
        lobId: resolvedLobId || undefined,
        onUploadProgress: (pct) => {
          runInAction(() => { this.uploadPct = pct })
        },
        signal: this.uploadController?.signal,
      })

      runInAction(() => {
        this.jobId = result.jobId
        this.step = 2
        this.startPolling()
      })
    } catch (e: any) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') {
        toast.info("Upload cancelled.")
        return
      }
      const responseData = e?.response?.data
      runInAction(() => {
        this.uploadError = {
          title: responseData?.message || 'Upload failed — something went wrong.',
          detail: responseData?.detail || 'Please verify your file format and target details and try again.',
        }
      })
    } finally {
      runInAction(() => {
        this.isUploading = false
        this.uploadController = null
      })
    }
  }

  cancelUpload() {
    if (this.uploadController) {
      this.uploadController.abort()
    }
  }

  async handleCancel() {
    if (!this.jobId) return
    try {
      await leadsApi.cancelBulkUpload(this.jobId)
      toast.info("Cancellation requested.")
      const job = await leadsApi.getBulkUploadStatus(this.jobId)
      runInAction(() => {
        this.jobStatus = job
        if (job.status === 'Cancelled') {
          this.step = 3
          this.stopPolling()
          this.stopTimer()
          this.fetchLogs(this.jobId!, 1)
          toast.warn("Bulk import has been cancelled.")
          queryClient.invalidateQueries({ queryKey: ['leads'] })
        }
      })
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to cancel job.")
    }
  }

  async fetchLogs(id: string, page: number) {
    try {
      const logs = await leadsApi.getBulkUploadLogs(id, page, 10)
      runInAction(() => {
        this.logsResult = logs
        this.logsPage = page
      })
    } catch (err) {
      // ignore
    }
  }

  startPolling() {
    this.stopPolling()
    this.stopTimer()

    const start = Date.now()
    runInAction(() => {
      this.elapsedSeconds = 0
    })

    this.timerId = setInterval(() => {
      runInAction(() => {
        this.elapsedSeconds = Math.floor((Date.now() - start) / 1000)
      })
    }, 1000)

    const poll = async () => {
      if (!this.jobId) return
      try {
        const job = await leadsApi.getBulkUploadStatus(this.jobId)
        runInAction(() => {
          this.jobStatus = job
          if (job.status === 'Completed' || job.status === 'Failed' || job.status === 'Cancelled') {
            this.stopPolling()
            this.stopTimer()
            this.step = 3
            this.fetchLogs(this.jobId!, 1)
            
            // Refresh parent lead table
            queryClient.invalidateQueries({ queryKey: ['leads'] })

            if (job.status === 'Cancelled') {
              toast.warn("Bulk import has been cancelled.")
            } else if (job.status === 'Completed') {
              toast.success("Bulk import completed successfully.")
            } else if (job.status === 'Failed') {
              toast.error("Bulk import failed.")
            }
          }
        })
      } catch (err) {
        // ignore
      }
    }

    poll()
    this.pollIntervalId = setInterval(poll, 3000)
  }

  private stopPolling() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId)
      this.pollIntervalId = null
    }
  }

  private stopTimer() {
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }
}

export const bulkUploadStore = new BulkUploadStore()
